import { createFunctionLibrary } from '@wix/thunderbolt-becky-root'
import { PlatformModel } from '@wix/thunderbolt-symbols'
import type { BeckyModel, SiteAssetsModule, PlatformInstanceModel } from '@wix/thunderbolt-becky-root'
import { resolveBeckyModel } from './helpers/resolveBeckyModel'
import byRefModule from '../byRef'
import { maybeAppendErrorPages } from '../errorPages/getPayloadWithErrorPage'
import { BeckyFetch } from './utils/beckyFetch'
import { getBeckyTopology } from './helpers/topologyBuilder'
import { platformModelFactory } from '@wix/thunderbolt-becky-carmi-ds/dist/carmiEntries/platform-simple'
import schemas from '@wix/document-services-json-schemas/dist/refData.json'
import { AsyncNodes, getAsyncStore } from '@wix/thunderbolt-async-state-manager-store'
import type { AsyncStores } from '@wix/thunderbolt-async-state-manager-store'
import { addInvalidation, fetchShapes } from '@wix/thunderbolt-fetch-shapes'

const platformModule: SiteAssetsModule<PlatformModel> = (moduleContext) => {
	const { httpClient, metricsReporter, topologyGetter, logger } = moduleContext
	const fetchFn = BeckyFetch(httpClient.fetch, metricsReporter, logger)
	const funcLib = createFunctionLibrary({
		fetch: fetchFn,
		metricsReporter,
		schemas,
	})
	const byRef = byRefModule(moduleContext)

	return {
		execute: async (payload) => {
			const beckyTopology = getBeckyTopology(topologyGetter, payload)

			return await byRef
				.execute({
					...maybeAppendErrorPages(payload),
					resolveGhosts: 'true',
				})
				.then((payloadWithResolvedRefs) =>
					resolveBeckyModel({
						payload: payloadWithResolvedRefs,
						beckyTopology,
						fetchFn,
						metricsReporter,
						logger,
					})
				)
				.then(async (beckyModel) => {
					const asyncNodes: AsyncNodes = [
						{
							addInvalidation,
							afterInvalidation: (shapes) => {
								return fetchShapes(
									shapes,
									fetchFn,
									beckyTopology.environmentTopology.mediaRootUrl,
									funcLib.buildSvgUrl,
									metricsReporter
								)
							},
							name: 'shapeStore',
						},
					]

					const { shapeStore } = (await funcLib.runAsyncAndReport(
						async () => getAsyncStore(asyncNodes, beckyModel, metricsReporter, schemas),
						'tb-platform-async-stores'
					)) as Pick<AsyncStores, 'shapeStore'>

					const beckyModelWithShapes: BeckyModel = { ...beckyModel, shapeStore }

					const platformFuncLib = { ...funcLib, setFetchRequestsStore: () => metricsReporter.reportError }
					return funcLib.runAndReport<PlatformInstanceModel>(() => {
						const ret = platformModelFactory(beckyModelWithShapes, platformFuncLib)
						return ret
					}, 'tb-platform-model-calculation')
				})
				.then((platformModel) => platformModel.platform)
		},
	}
}

export default platformModule
